import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { IMQTTBrokerContextProps } from "./MQTTBrokerContextInterfaces";
import { MQTTBrokerContext } from "./MQTTBrokerContext";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";
import { IEventReportValues } from "mqtt/entities/EventReport/EventReportInterfaces";
import { EventReportMQTTEntity } from "mqtt/entities/EventReport/EventReportEntity";
import { IClusterReorderingValues } from "mqtt/entities/ClusterReordering/ClusterReorderingInterfaces";
import { ClusterReorderingMQTTEntity } from "mqtt/entities/ClusterReordering/ClusterReorderingEntity";
import { IMQTTBrokerSettings } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { Alert, Snackbar } from "@mui/material";
import { useTranslation } from "react-i18next";
import { initialState } from "./MQTTBrokerContext";
import { ClusterCachedDataContext } from "contexts/ClusterCachedDataContext/ClusterCachedDataContext";
import useMQTTKeepAlive from "hooks/useMQTTKeepAlive";

const defaultOptionId = "DEFAULT_OPTION_ID";
const defaultOptionName = "Default";

const defaultOption: IMQTTBrokerSettings = {
    allow_anonymous: process.env.REACT_APP_MOSQUITTO_ALLOW_ANONYMOUS === "TRUE",
    host: process.env.REACT_APP_MOSQUITTO_BROKER_URL || "mqtt://test.mosquitto.org:8081",
    user: process.env.REACT_APP_MOSQUITTO_BROKER_USER,
    password: process.env.REACT_APP_MOSQUITTO_BROKER_PASSWORD,
    id: defaultOptionId,
    name: defaultOptionName,
};

export const MQTTBrokerContextProvider: React.FC<IMQTTBrokerContextProps> = ({ children }) => {
    const { t } = useTranslation();
    const {
        store: { mqttBrokerSettings: mqttBrokerSettingsHMI, machineCenter: machineCenterHMI },
    } = useContext(HMICachedDataContext);
    const {
        store: { mqttBrokerSettings: mqttBrokerSettingsCluster, clusterDetails },
    } = useContext(ClusterCachedDataContext);
    const mqttBrokerSettings = useMemo(
        () => mqttBrokerSettingsHMI || mqttBrokerSettingsCluster,
        [mqttBrokerSettingsHMI, mqttBrokerSettingsCluster],
    );
    const code = useMemo(
        () => machineCenterHMI?.hmi_code || clusterDetails?.cluster_code,
        [machineCenterHMI, clusterDetails],
    );
    const dedicatedLocalserver = useMemo(
        () =>
            Boolean(
                machineCenterHMI?.production_line?.mill?.dedicated_localserver || clusterDetails?.dedicated_localserver,
            ),
        [machineCenterHMI, clusterDetails],
    );
    const [open, setOpen] = useState(false);
    const [openMQTTSettingDialog, setOpenMQTTSettingDialog] = useState<boolean>(initialState.openMQTTSettingDialog);
    const secretTapCountRef = useRef(0);
    const lastSecretTapTimeRef = useRef<number | null>(null);

    const currentSetting = useMemo(() => {
        if (mqttBrokerSettings && mqttBrokerSettings.length > 0) {
            return mqttBrokerSettings[0];
        }
        return defaultOption;
    }, [mqttBrokerSettings]);

    const publishEventReport = useCallback(
        async (value: IEventReportValues) => {
            try {
                await EventReportMQTTEntity.publishEventReport(value, currentSetting, code);
            } catch (error) {
                console.log("Event error:", error);
                setOpen(true);
                throw error;
            }
        },
        [currentSetting, code],
    );

    const publishClusterReordering = useCallback(
        async (values: IClusterReorderingValues) => {
            try {
                await ClusterReorderingMQTTEntity.publishClusterReordering(values, currentSetting, code);
            } catch (error) {
                setOpen(true);
                throw error;
            }
        },
        [currentSetting, code],
    );

    const handleOpenMQTTSettingDialog = (value: boolean) => {
        setOpenMQTTSettingDialog(value);
    };

    const handleSecretLogoTap = useCallback(() => {
        const now = Date.now();
        const last = lastSecretTapTimeRef.current;
        if (last && now - last <= 800) {
            secretTapCountRef.current += 1;
        } else {
            secretTapCountRef.current = 1;
        }
        lastSecretTapTimeRef.current = now;

        if (secretTapCountRef.current >= 5) {
            secretTapCountRef.current = 0;
            setOpenMQTTSettingDialog(true);
        }
    }, []);

    const { isLocalServerActive, isBrokerConnected } = useMQTTKeepAlive(dedicatedLocalserver, currentSetting, code);

    const providerValue = {
        publishEventReport,
        publishClusterReordering,
        currentSetting,
        openMQTTSettingDialog,
        handleOpenMQTTSettingDialog,
        handleSecretLogoTap,
        isLocalServerActive: isLocalServerActive,
        isBrokerConnected: isBrokerConnected,
    };

    return (
        <MQTTBrokerContext.Provider value={providerValue}>
            <Snackbar
                open={open}
                autoHideDuration={6000}
                onClose={() => setOpen(false)}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert severity="error" sx={{ width: "100%" }}>
                    <span style={{ whiteSpace: "pre-line" }}>{t("mqtt_broker_provider.error")}</span>
                </Alert>
            </Snackbar>
            {children}
        </MQTTBrokerContext.Provider>
    );
};
